// Resolve micro-interaction sequencing (docs/06-ux-rules.md "Resolve
// animation", R2/Q5; 11-chassis-plan.md §2 PR 04; prototype/v69's
// `resolveFade` / `resolveCheckPop` / `resolveRowExit` / `resPlus1`
// keyframes, index.html:1359-1375,2645-2668).
//
// Two sequences, both starting the instant a row's status flips open →
// resolved (label flips immediately in both — this hook only drives the
// VISUAL phase, never the label):
//
//   stillShows (row still matches the current filter, e.g. Status=All/
//   Resolved): green wash that fades out over RESOLVE_WASH_MS. The Resolved
//   segment pulses at RESOLVE_SWIPE_MS (the exact 700ms v83's
//   `markResolved()` hardcodes before `flashResolvedPlusOne`), while the
//   wash keeps fading; the row settles back to idle once it ends.
//
//   leaving (row no longer matches, e.g. Status=Open): check-pop for
//   RESOLVE_HOLD_MS (650ms hold, during which the .5s check-pop animation
//   plays), then swipe-out; the row is considered gone RESOLVE_ROW_HIDE_MS
//   (820ms) after the swipe starts — `onExited` fires then, followed by the
//   Resolved-segment pulse.
//
// `prefers-reduced-motion` (docs/06-ux-rules.md, repo convention via
// `usePrefersReducedMotion`/`testUtils/matchMedia.ts`): skips straight to
// the end state — no intermediate phases are ever set, `onExited` (if
// leaving) and `onPulseResolved` fire synchronously.
import usePrefersReducedMotion from 'hooks/usePrefersReducedMotion'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  RESOLVE_HOLD_MS,
  RESOLVE_ROW_HIDE_MS,
  RESOLVE_SWIPE_MS,
  RESOLVE_WASH_MS,
} from '../constants'

export type ResolveAnimationPhase = 'idle' | 'flash' | 'checking' | 'exiting'

export interface UseResolveAnimationOptions {
  // Called once the row has fully left the list (only for the "leaving"
  // sequence) — the caller drops it from whatever it renders and should
  // auto-advance selection / announce here.
  onExited: (itemId: string) => void
  // Fires the Resolved-segment +1: at RESOLVE_SWIPE_MS during an in-place
  // flash, or right after onExited when the row leaves.
  onPulseResolved: () => void
}

export interface UseResolveAnimationResult {
  getPhase: (itemId: string) => ResolveAnimationPhase
  // `stillShows`: whether the row still matches the current filter — the
  // caller (the container) computes this, since only it knows the active
  // filter params.
  startResolve: (itemId: string, stillShows: boolean) => void
  // Stops an in-flight sequence for one item: clears its timers, resets its
  // phase to idle, and suppresses its onExited/onPulseResolved. A no-op for
  // an item with no in-flight sequence.
  cancel: (itemId: string) => void
}

export function useResolveAnimation({
  onExited,
  onPulseResolved,
}: UseResolveAnimationOptions): UseResolveAnimationResult {
  const [phases, setPhases] = useState<Record<string, ResolveAnimationPhase>>(
    {}
  )
  // Keyed per item so `cancel` can clear one sequence's timers without
  // touching another's.
  const timeoutsRef = useRef(new Map<string, ReturnType<typeof setTimeout>[]>())
  const reducedMotion = usePrefersReducedMotion()

  // Callbacks are read via refs so `startResolve` (below) never needs them
  // in its own dependency array — a caller that passes a fresh inline
  // function every render (the common case for `onExited`/`onPulseResolved`
  // closing over container state) shouldn't invalidate in-flight timers.
  const onExitedRef = useRef(onExited)
  onExitedRef.current = onExited
  const onPulseResolvedRef = useRef(onPulseResolved)
  onPulseResolvedRef.current = onPulseResolved

  useEffect(
    () => () => {
      timeoutsRef.current.forEach(timeouts => timeouts.forEach(clearTimeout))
    },
    []
  )

  const clearTimers = useCallback((itemId: string) => {
    const timeouts = timeoutsRef.current.get(itemId)
    if (timeouts) timeouts.forEach(clearTimeout)
    timeoutsRef.current.delete(itemId)
  }, [])

  const schedule = useCallback((itemId: string, fn: () => void, ms: number) => {
    const timeout = setTimeout(fn, ms)
    const timeouts = timeoutsRef.current.get(itemId) ?? []
    timeouts.push(timeout)
    timeoutsRef.current.set(itemId, timeouts)
    return timeout
  }, [])

  const cancel = useCallback(
    (itemId: string) => {
      clearTimers(itemId)
      setPhases(current => {
        if (!(itemId in current)) return current
        const next = { ...current }
        delete next[itemId]
        return next
      })
    },
    [clearTimers]
  )

  const startResolve = useCallback(
    (itemId: string, stillShows: boolean) => {
      clearTimers(itemId)

      if (reducedMotion) {
        if (!stillShows) onExitedRef.current(itemId)
        onPulseResolvedRef.current()
        return
      }

      if (stillShows) {
        setPhases(current => ({ ...current, [itemId]: 'flash' }))
        schedule(itemId, () => onPulseResolvedRef.current(), RESOLVE_SWIPE_MS)
        schedule(
          itemId,
          () => {
            setPhases(current => ({ ...current, [itemId]: 'idle' }))
            clearTimers(itemId)
          },
          RESOLVE_WASH_MS
        )
        return
      }

      setPhases(current => ({ ...current, [itemId]: 'checking' }))
      schedule(
        itemId,
        () => {
          setPhases(current => ({ ...current, [itemId]: 'exiting' }))
          schedule(
            itemId,
            () => {
              setPhases(current => {
                const next = { ...current }
                delete next[itemId]
                return next
              })
              clearTimers(itemId)
              onExitedRef.current(itemId)
              onPulseResolvedRef.current()
            },
            RESOLVE_ROW_HIDE_MS
          )
        },
        RESOLVE_HOLD_MS
      )
    },
    [reducedMotion, schedule, clearTimers]
  )

  const getPhase = useCallback(
    (itemId: string): ResolveAnimationPhase => phases[itemId] ?? 'idle',
    [phases]
  )

  return { getPhase, startResolve, cancel }
}
